import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TestCalculator from '@/components/assessment/TestCalculator';

const API = process.env.REACT_APP_API_URL || '';

const CAT_LABEL = { english: 'English', maths: 'Mathematics', logical: 'Logical', reading: 'Reading' };
const CAT_ORDER  = ['english', 'maths', 'logical', 'reading'];
const MAX_VIOLATIONS = 3;

export default function AssessmentTest() {
  const navigate = useNavigate();

  const [questions,      setQuestions]      = useState([]);
  const [answers,        setAnswers]        = useState({});
  const [reviews,        setReviews]        = useState({});
  const [currentIdx,     setCurrentIdx]     = useState(0);
  const [secondsLeft,    setSecondsLeft]    = useState(0);
  const [loading,        setLoading]        = useState(true);
  const [submitting,     setSubmitting]     = useState(false);
  const [showConfirm,    setShowConfirm]    = useState(false);
  const [error,          setError]          = useState('');
  const [violations,     setViolations]     = useState(0);
  const [showViolation,  setShowViolation]  = useState(false);

  const submittingRef  = useRef(false);
  const answersRef     = useRef({});
  const timerIntervalRef = useRef(null);
  const violationsRef  = useRef(0);
  const doSubmitRef    = useRef(null);   // always points to latest doSubmit

  const attemptId = useRef(parseInt(localStorage.getItem('finirio_assess_id') || '0'));
  const token     = useRef(localStorage.getItem('finirio_assess_tok') || '');

  // ── Mount: load from localStorage ─────────────────────────────────────────
  useEffect(() => {
    if (!attemptId.current || !token.current) { navigate('/assessment'); return; }
    const qs     = JSON.parse(localStorage.getItem('finirio_assess_qs')        || '[]');
    const expStr = localStorage.getItem('finirio_assess_exp') || '';
    const savedA = JSON.parse(localStorage.getItem('finirio_assess_saved_ans') || '{}');
    const savedR = JSON.parse(localStorage.getItem('finirio_assess_saved_rev') || '{}');

    if (!qs.length || !expStr) { navigate('/assessment/rules'); return; }

    const ansMap = {}, revMap = {};
    Object.entries(savedA).forEach(([k, v]) => { if (v) ansMap[+k] = v; });
    Object.entries(savedR).forEach(([k, v]) => { revMap[+k] = !!v; });

    setQuestions(qs);
    setAnswers(ansMap);
    setReviews(revMap);
    answersRef.current = ansMap;

    const rem = Math.max(0, Math.floor((new Date(expStr) - Date.now()) / 1000));
    setSecondsLeft(rem);
    setLoading(false);

    if (rem === 0) doSubmit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (loading) return;
    const expStr = localStorage.getItem('finirio_assess_exp') || '';
    if (!expStr) return;

    timerIntervalRef.current = setInterval(() => {
      const rem = Math.max(0, Math.floor((new Date(expStr) - Date.now()) / 1000));
      setSecondsLeft(rem);
      if (rem === 0) {
        clearInterval(timerIntervalRef.current);
        doSubmit();
      }
    }, 1000);

    return () => clearInterval(timerIntervalRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ── Anti-cheat: keyboard & mouse lockdown ──────────────────────────────────
  useEffect(() => {
    const blockContext = (e) => e.preventDefault();

    const blockKey = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      // Block copy, select-all, view-source, devtools shortcuts
      if (ctrl && ['c','a','u','s','p'].includes(e.key.toLowerCase())) { e.preventDefault(); return; }
      if (ctrl && e.shiftKey && ['i','j','c'].includes(e.key.toLowerCase())) { e.preventDefault(); return; }
      if (e.key === 'F12') { e.preventDefault(); return; }
    };

    const blockSelect = (e) => e.preventDefault();
    const blockCopy   = (e) => e.preventDefault();

    document.addEventListener('contextmenu',   blockContext, true);
    document.addEventListener('keydown',        blockKey,    true);
    document.addEventListener('selectstart',    blockSelect, true);
    document.addEventListener('copy',           blockCopy,   true);
    document.addEventListener('cut',            blockCopy,   true);

    return () => {
      document.removeEventListener('contextmenu',  blockContext, true);
      document.removeEventListener('keydown',       blockKey,    true);
      document.removeEventListener('selectstart',   blockSelect, true);
      document.removeEventListener('copy',          blockCopy,   true);
      document.removeEventListener('cut',           blockCopy,   true);
    };
  }, []);

  // ── Anti-cheat: tab-switch / window blur detection ─────────────────────────
  useEffect(() => {
    const handleHidden = () => {
      if (document.visibilityState !== 'hidden') return;

      const newCount = violationsRef.current + 1;
      violationsRef.current = newCount;
      setViolations(newCount);
      setShowViolation(true);

      // Log to backend (fire-and-forget, never block)
      try {
        axios.post(`${API}/api/assessment/log-event`, {
          attempt_id:    attemptId.current,
          session_token: token.current,
          event_type:    'tab_switch',
          event_data:    { violation_number: newCount, ts: new Date().toISOString() },
        }).catch(() => {});
      } catch (_) {}

      if (newCount >= MAX_VIOLATIONS) {
        // Auto-submit after brief delay so state can paint
        setTimeout(() => { doSubmitRef.current?.(); }, 800);
      }
    };

    document.addEventListener('visibilitychange', handleHidden);
    return () => document.removeEventListener('visibilitychange', handleHidden);
  }, []);

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2,'0')}:${String(s % 60).padStart(2,'0')}`;

  // ── Save answer to server ──────────────────────────────────────────────────
  const saveToServer = useCallback(async (qid, answer, review) => {
    try {
      await axios.post(`${API}/api/assessment/save-answer`, {
        attempt_id:       attemptId.current,
        session_token:    token.current,
        question_id:      qid,
        selected_answer:  answer || null,
        is_marked_review: review || false,
      });
    } catch (_) { /* silent — progress is safe on server */ }
  }, []);

  const selectAnswer = (qid, opt) => {
    const next = { ...answersRef.current, [qid]: opt };
    setAnswers(next);
    answersRef.current = next;
    saveToServer(qid, opt, reviews[qid] || false);
  };

  const toggleReview = qid => {
    const r = !reviews[qid];
    setReviews(prev => ({ ...prev, [qid]: r }));
    saveToServer(qid, answersRef.current[qid] || null, r);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const doSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setShowConfirm(false);
    setShowViolation(false);
    clearInterval(timerIntervalRef.current);
    try {
      await axios.post(`${API}/api/assessment/submit`, {
        attempt_id: attemptId.current, session_token: token.current,
      });
      navigate('/assessment/result');
    } catch (err) {
      if (err.response?.data?.detail?.includes('already')) {
        navigate('/assessment/result');
      } else {
        setError('Submission failed. Check your internet and try again.');
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  // Keep doSubmitRef in sync with latest doSubmit every render
  doSubmitRef.current = doSubmit;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <p className="text-slate-500">Loading your test…</p>
    </div>
  );

  const q = questions[currentIdx];
  if (!q) return null;

  const isMaths = q.category === 'maths';

  // Sections in order
  const sectionOrder = CAT_ORDER.filter(c => questions.some(x => x.category === c));
  const sectionStart = {};
  let si = 0;
  sectionOrder.forEach(c => {
    sectionStart[c] = si;
    si += questions.filter(x => x.category === c).length;
  });

  const answered   = Object.values(answers).filter(Boolean).length;
  const reviewCnt  = Object.values(reviews).filter(Boolean).length;
  const unanswered = questions.length - answered;
  const timerColor = secondsLeft <= 60 ? 'text-red-400' : secondsLeft <= 300 ? 'text-amber-300' : 'text-white';

  return (
    <div className="min-h-screen flex flex-col bg-slate-100"
      style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' }}>

      {/* ── Tab-switch violation overlay ─────────────────────────────────── */}
      {showViolation && violations < MAX_VIOLATIONS && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-red-900/95">
          <div className="text-center px-8 max-w-md">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-3xl font-bold text-white mb-3">Tab Switch Detected!</h2>
            <p className="text-red-200 text-lg mb-2">Do not leave the test window.</p>
            <p className="text-red-100 text-sm mb-6">
              Opening other tabs, windows or applications is not allowed during the test.
            </p>
            <div className="bg-red-800/60 rounded-xl px-6 py-3 mb-6 inline-block">
              <p className="text-white font-semibold text-lg">
                Violation {violations} of {MAX_VIOLATIONS}
              </p>
              <p className="text-red-300 text-xs mt-0.5">
                After {MAX_VIOLATIONS} violations your test will be auto-submitted.
              </p>
            </div>
            <br />
            <button
              onClick={() => setShowViolation(false)}
              className="px-8 h-12 rounded-xl bg-white text-red-700 font-bold text-sm hover:bg-red-50 transition">
              Return to Test
            </button>
          </div>
        </div>
      )}

      {/* ── Auto-submit overlay (3rd violation) ──────────────────────────── */}
      {showViolation && violations >= MAX_VIOLATIONS && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-red-900/95">
          <div className="text-center px-8 max-w-md">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-white mb-3">Test Auto-Submitted</h2>
            <p className="text-red-200 mb-2">
              You left the test window {MAX_VIOLATIONS} times. Your test has been automatically submitted with the answers saved so far.
            </p>
            {submitting && (
              <p className="text-red-300 text-sm animate-pulse mt-4">Submitting…</p>
            )}
          </div>
        </div>
      )}

      {/* ── Top bar ────────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-4 py-2 shadow-lg"
        style={{ background: '#0f3d78' }}>
        <div className="flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_site-creator-3034/artifacts/pbfdilsv_Finirio.JPG"
            alt="Finirio" className="h-7 object-contain brightness-0 invert opacity-90" />
          <span className="hidden sm:inline text-white/50 text-sm">Aptitude Assessment</span>
        </div>

        <div className={`font-mono font-bold text-xl tracking-wider ${timerColor}`}>
          ⏱ {fmt(secondsLeft)}
          {secondsLeft <= 60 && secondsLeft > 0 && (
            <span className="ml-2 text-xs font-sans animate-pulse">Hurry!</span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {violations > 0 && (
            <span className="hidden sm:flex items-center gap-1 text-red-300 text-xs font-medium">
              ⚠️ {violations}/{MAX_VIOLATIONS} violations
            </span>
          )}
          <button onClick={() => setShowConfirm(true)} disabled={submitting}
            className="px-4 h-9 rounded-lg text-sm font-semibold transition hover:brightness-110 disabled:opacity-50"
            style={{ backgroundColor: '#2ea690' }}>
            {submitting ? 'Submitting…' : 'Submit Test ✓'}
          </button>
        </div>
      </div>

      {/* ── Section tabs ─────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 flex overflow-x-auto">
        {sectionOrder.map(c => {
          const cnt    = questions.filter(x => x.category === c).length;
          const active = q.category === c;
          return (
            <button key={c}
              onClick={() => setCurrentIdx(sectionStart[c])}
              className={`px-5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                active ? 'border-[#1e73be] text-[#1e73be]' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}>
              {CAT_LABEL[c]} ({cnt})
              {c === 'maths' && <span className="ml-1 text-[10px] text-[#2ea690]">🧮</span>}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-red-50 border-b border-red-200 text-red-700 text-sm px-4 py-2 text-center">
          {error}
        </div>
      )}

      {/* ── Content ──────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4 max-w-[1300px] mx-auto w-full">

        {/* Question panel */}
        <div className="flex-1 flex flex-col gap-3 min-w-0">
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex-1">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: '#eaf3fb', color: '#1e73be' }}>
                {CAT_LABEL[q.category] || q.category}
              </span>
              {q.sub_category && <span className="text-xs text-slate-400">{q.sub_category}</span>}
              <span className="ml-auto text-xs text-slate-400">Question {currentIdx + 1} of {questions.length}</span>
            </div>

            {/* Passage */}
            {q.passage && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-100 rounded-lg text-sm text-slate-700 leading-relaxed">
                <p className="text-xs font-semibold text-amber-700 mb-2">📖 Read the following passage:</p>
                <p>{q.passage}</p>
              </div>
            )}

            {/* Question text */}
            <p className="text-slate-800 font-medium leading-relaxed mb-4 whitespace-pre-line">
              {q.question_text}
            </p>

            {/* Options */}
            <div className="space-y-2">
              {['A', 'B', 'C', 'D'].map(opt => {
                const sel = answers[q.id] === opt;
                return (
                  <label key={opt}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition ${
                      sel ? 'border-[#1e73be] bg-[#eaf3fb]' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    onClick={() => selectAnswer(q.id, opt)}>
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold shrink-0 transition ${
                      sel ? 'border-[#1e73be] bg-[#1e73be] text-white' : 'border-slate-300 text-slate-400'
                    }`}>{opt}</div>
                    <span className={`text-sm ${sel ? 'text-[#1e73be] font-semibold' : 'text-slate-700'}`}>
                      {q.options[opt]}
                    </span>
                    <input type="radio" className="sr-only" readOnly checked={sel} />
                  </label>
                );
              })}
            </div>
          </div>

          {/* Navigation bar */}
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between gap-2">
            <button onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
              disabled={currentIdx === 0}
              className="px-5 h-10 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition disabled:opacity-40">
              ← Prev
            </button>

            <button onClick={() => toggleReview(q.id)}
              className={`px-4 h-10 rounded-lg text-sm font-medium transition border ${
                reviews[q.id]
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'border-amber-400 text-amber-600 hover:bg-amber-50'
              }`}>
              🔖 {reviews[q.id] ? 'Marked' : 'Mark for Review'}
            </button>

            {currentIdx < questions.length - 1 ? (
              <button onClick={() => setCurrentIdx(i => i + 1)}
                className="px-5 h-10 rounded-lg text-white text-sm font-medium hover:brightness-110 transition"
                style={{ backgroundColor: '#1e73be' }}>
                Next →
              </button>
            ) : (
              <button onClick={() => setShowConfirm(true)}
                className="px-5 h-10 rounded-lg text-white text-sm font-medium hover:brightness-110 transition"
                style={{ backgroundColor: '#2ea690' }}>
                Finish ✓
              </button>
            )}
          </div>
        </div>

        {/* Right sidebar: Navigator + Calculator */}
        <div className="w-full lg:w-60 flex-shrink-0">
          <div className="lg:sticky lg:top-20 space-y-0">

            {/* Question Navigator */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Question Navigator</h4>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-1 mb-4 text-center">
                <div className="bg-green-50 rounded-lg p-1.5">
                  <p className="text-lg font-bold text-green-600">{answered}</p>
                  <p className="text-[10px] text-green-600">Done</p>
                </div>
                <div className="bg-red-50 rounded-lg p-1.5">
                  <p className="text-lg font-bold text-red-500">{unanswered}</p>
                  <p className="text-[10px] text-red-500">Left</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-1.5">
                  <p className="text-lg font-bold text-amber-500">{reviewCnt}</p>
                  <p className="text-[10px] text-amber-500">Review</p>
                </div>
              </div>

              {/* Section grids */}
              {sectionOrder.map(cat => (
                <div key={cat} className="mb-3">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                    {CAT_LABEL[cat]}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {questions.map((item, i) => {
                      if (item.category !== cat) return null;
                      const isCur = i === currentIdx;
                      const isAns = !!answers[item.id];
                      const isRev = !!reviews[item.id];
                      let cls = 'w-8 h-8 text-xs rounded-lg font-semibold border transition ';
                      if (isCur)      cls += 'bg-[#1e73be] text-white border-[#1e73be]';
                      else if (isRev) cls += 'bg-amber-400 text-white border-amber-400';
                      else if (isAns) cls += 'bg-green-500 text-white border-green-500';
                      else            cls += 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50';
                      return (
                        <button key={i} className={cls} onClick={() => setCurrentIdx(i)}>
                          {i + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Legend */}
              <div className="mt-3 pt-3 border-t space-y-1">
                {[
                  ['bg-[#1e73be]',  'Current'],
                  ['bg-green-500',  'Answered'],
                  ['bg-amber-400',  'For Review'],
                  ['bg-white border border-slate-200', 'Not Visited'],
                ].map(([bg, lbl]) => (
                  <div key={lbl} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded ${bg}`} />
                    <span className="text-[11px] text-slate-500">{lbl}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Calculator — only on Maths section */}
            {isMaths && <TestCalculator />}

          </div>
        </div>
      </div>

      {/* ── Confirm modal ──────────────────────────────────────────────────── */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Submit Test?</h3>
            <div className="grid grid-cols-3 gap-2 mb-4 text-center">
              <div className="bg-green-50 rounded-lg p-2">
                <p className="text-xl font-bold text-green-600">{answered}</p>
                <p className="text-xs text-green-600">Answered</p>
              </div>
              <div className="bg-red-50 rounded-lg p-2">
                <p className="text-xl font-bold text-red-500">{unanswered}</p>
                <p className="text-xs text-red-500">Unanswered</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-2">
                <p className="text-xl font-bold text-amber-500">{reviewCnt}</p>
                <p className="text-xs text-amber-500">Review</p>
              </div>
            </div>
            <p className="text-xs text-red-600 mb-4">⚠️ Once submitted, you cannot change your answers.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirm(false)}
                className="flex-1 h-11 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition">
                Cancel
              </button>
              <button onClick={doSubmit} disabled={submitting}
                className="flex-1 h-11 rounded-lg text-white text-sm font-semibold hover:brightness-110 transition disabled:opacity-60"
                style={{ backgroundColor: '#2ea690' }}>
                {submitting ? 'Submitting…' : 'Yes, Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
